import React from 'react';
import { motion } from 'framer-motion';
import { Flame, TrendingUp, TrendingDown, Sparkles } from 'lucide-react';
import { getTrendingCoins, getTopGainers, getTopLosers } from '../../utils/mockDataGenerator';

const TrendingBar: React.FC = () => {
  const trendingCoins = getTrendingCoins();
  const topGainers = getTopGainers();
  const topLosers = getTopLosers();


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="mb-8"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        {/* Trending */}
        <div className="glass-effect rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center text-zinc-300">
              <Flame className="w-4 h-4 mr-2 text-orange-500" />
              Trending
            </h3>
            <a href="/trending" className="text-xs text-zinc-500 hover:text-white transition-colors">
              View more →
            </a>
          </div>
          <div className="flex space-x-4 overflow-x-auto">
            {trendingCoins.slice(0, 3).map((coin, index) => (
              <div
                key={coin.id}
                className="flex items-center space-x-2 bg-zinc-900/50 rounded-lg px-3 py-2 min-w-fit"
              >
                <span className="text-xs text-zinc-500">#{index + 1}</span>
                <img src={coin.image} alt={coin.name} className="w-4 h-4 sm:w-5 sm:h-5 rounded-full" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium">{coin.symbol.toUpperCase()}</span>
                  <span className={`text-xs ${
                    coin.price_change_percentage_24h >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {coin.price_change_percentage_24h >= 0 ? '+' : ''}
                    {coin.price_change_percentage_24h.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Gainers */}
        <div className="glass-effect rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center text-zinc-300">
              <TrendingUp className="w-4 h-4 mr-2 text-green-500" />
              Top Gainers
            </h3>
            <a href="/gainers" className="text-xs text-zinc-500 hover:text-white transition-colors">
              View more →
            </a>
          </div>
          <div className="flex space-x-4 overflow-x-auto">
            {topGainers.slice(0, 3).map((coin) => (
              <div
                key={coin.id}
                className="flex items-center space-x-2 bg-zinc-900/50 rounded-lg px-3 py-2 min-w-fit"
              >
                <img src={coin.image} alt={coin.name} className="w-4 h-4 sm:w-5 sm:h-5 rounded-full" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium">{coin.symbol.toUpperCase()}</span>
                  <span className="text-xs text-green-500 font-semibold">
                    +{coin.price_change_percentage_24h.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Losers */}
        <div className="glass-effect rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center text-zinc-300">
              <TrendingDown className="w-4 h-4 mr-2 text-red-500" />
              Top Losers
            </h3>
            <a href="/losers" className="text-xs text-zinc-500 hover:text-white transition-colors">
              View more →
            </a>
          </div>
          <div className="flex space-x-4 overflow-x-auto">
            {topLosers.slice(0, 3).map((coin) => (
              <div
                key={coin.id}
                className="flex items-center space-x-2 bg-zinc-900/50 rounded-lg px-3 py-2 min-w-fit"
              >
                <img src={coin.image} alt={coin.name} className="w-4 h-4 sm:w-5 sm:h-5 rounded-full" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium">{coin.symbol.toUpperCase()}</span>
                  <span className="text-xs text-red-500 font-semibold">
                    {coin.price_change_percentage_24h.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional quick stats */}
      <div className="mt-4 flex flex-wrap gap-4">
        <div className="flex items-center space-x-2 text-sm">
          <Sparkles className="w-4 h-4 text-violet-500" />
          <span className="text-zinc-400">Fear & Greed Index:</span>
          <span className="font-semibold text-green-500">72 (Greed)</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <span className="text-zinc-400">Trending Searches:</span>
          <span className="text-zinc-300">PEPE</span>
          <span className="text-zinc-500">•</span>
          <span className="text-zinc-300">BONK</span>
          <span className="text-zinc-500">•</span>
          <span className="text-zinc-300">SOL</span>
        </div>
      </div>
    </motion.div>
  );
};

export default TrendingBar;